<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import I18n from '@core/components/I18n.vue'
import { themeConfig } from '@themeConfig'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const type = route.params.type as string
const appId = route.params.appId as string
const content = ref('')
const isLoading = ref(true)
const tenantInfo = ref<any>(null)
const { t, locale } = useI18n({ useScope: 'global' })
const publicLanguages = themeConfig.app.i18n.langConfig

const titleKey = computed(() => `publicSite.legal.titles.${type}`)
const localizedTitle = computed(() => {
  const translated = t(titleKey.value)
  return translated === titleKey.value ? t('publicSite.legal.titles.fallback') : translated
})

const localizedDate = computed(() =>
  new Intl.DateTimeFormat(locale.value || 'it', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date()),
)

const fetchLegal = async () => {
  try {
    // We pass X-App-ID header to get the correct tenant content
    const { data } = await useApi<any>(`/public/tenant/legal/${type}`, {
      headers: {
        'X-App-ID': appId,
        'X-Locale': locale.value,
        'Accept-Language': locale.value,
      }
    })
    
    content.value = data.value.content || `<p class="text-center">${t('publicSite.legal.tenantContentUnavailable')}</p>`
    
    // Also fetch basic tenant info for the UI
    const { data: configData } = await useApi<any>(`/public/tenant/mobile-app-settings`, {
      headers: {
        'X-App-ID': appId,
        'X-Locale': locale.value,
        'Accept-Language': locale.value,
      }
    })
    if (configData.value) {
      tenantInfo.value = configData.value
    }

  } catch (e) {
    content.value = `<p class="text-center text-error">${t('publicSite.legal.tenantLoadingError')}</p>`
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchLegal)
watch(locale, fetchLegal)
</script>

<template>
  <div class="legal-premium-page">
    <!-- Navbar (Identical to Landing) -->
    <nav class="nav-dark">
      <div class="container d-flex align-center">
        <div class="logo-area">
          <div v-if="tenantInfo?.mobile_app_settings?.theme?.logo" class="logo-img-wrapper mr-3">
             <img :src="tenantInfo.mobile_app_settings.theme.logo" alt="Logo" class="tenant-logo">
          </div>
          <div v-else class="logo-icon">
            <VIcon icon="tabler-rocket" color="white" size="28" />
          </div>
          <span class="logo-text">{{ tenantInfo?.name || 'Appforge' }}</span>
        </div>
        
        <VSpacer />
        
        <div class="nav-links d-none d-md-flex align-center">
          <a :href="`/legal/${appId}/privacy`" class="nav-link" :class="{ 'active': type === 'privacy' }">{{ t('publicSite.legal.nav.privacy') }}</a>
          <a :href="`/legal/${appId}/terms`" class="nav-link" :class="{ 'active': type === 'terms' }">{{ t('publicSite.legal.nav.terms') }}</a>
        </div>
        
        <VSpacer />
        
        <div class="nav-actions d-flex align-center">
          <I18n :languages="publicLanguages" class="me-2" />
          <VBtn color="primary" variant="tonal" icon="tabler-share" class="mr-2" />
        </div>
      </div>
    </nav>

    <div class="legal-content-wrapper">
      <div class="container py-16">
        <VCard class="legal-card animate-fade-in">
          <div class="legal-header pa-8 pa-md-16">
            <h1 class="text-h1 font-weight-black gradient-text mb-4">{{ localizedTitle }}</h1>
            <p class="text-h5 opacity-60 font-weight-regular">
              {{ t('publicSite.shared.lastUpdated') }}: {{ localizedDate }}
            </p>
          </div>

          <VDivider class="border-white-opacity-10" />

          <div class="pa-8 pa-md-16">
            <div v-if="isLoading" class="text-center py-16">
              <VProgressCircular indeterminate color="primary" size="64" />
            </div>

            <div v-else class="legal-body-html" v-html="content"></div>
          </div>

          <VDivider class="border-white-opacity-10" />

          <div class="pa-8 text-center opacity-40 text-caption">
            © {{ new Date().getFullYear() }} {{ tenantInfo?.name || 'Appforge.it' }} - {{ t('publicSite.shared.allRightsReserved') }}
          </div>
        </VCard>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800&display=swap');

.legal-premium-page {
  font-family: 'Outfit', sans-serif;
  background-color: #0F172A;
  color: #F1F5F9;
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Navbar */
.nav-dark {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 80px;
  display: flex;
  align-items: center;
  z-index: 1000;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.logo-area {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #6366F1 0%, #A855F7 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tenant-logo {
  height: 40px;
  max-width: 150px;
  object-fit: contain;
}

.logo-text {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -1px;
  color: white;
}

.nav-links {
  gap: 32px !important;
}

.nav-link {
  color: #CBD5E1;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  transition: all 0.3s ease;
  position: relative;
}

.nav-link:hover, .nav-link.active {
  color: white;
}

.legal-content-wrapper {
  padding-top: 80px;
}

.legal-card {
  background: rgba(30, 41, 59, 0.5) !important;
  backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 32px;
}

.gradient-text {
  background: linear-gradient(135deg, #818CF8 0%, #C084FC 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.legal-body-html {
  line-height: 1.8;
  font-size: 1.1rem;
  color: #CBD5E1;
}

.legal-body-html :deep(h1), .legal-body-html :deep(h2), .legal-body-html :deep(h3) {
  color: white;
  margin-top: 2.5rem;
  margin-bottom: 1rem;
}

.legal-body-html :deep(p) {
  margin-bottom: 1.5rem;
}

.animate-fade-in {
  animation: fadeIn 0.6s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
